import { isPlatform } from '@ionic/react'
import { ParseKeys } from 'i18next'
import {
  DiskTypeEnum,
  DocumentsTypeEnum,
  IncidentResponsibleEnum,
  LocaleEnum,
  RubberTypeEnum,
  WheelCodeEnum,
} from '~/shared/config/enums'

export const AUTH_TOKEN_STORAGE_KEY = 'auth_token'
export const AUTH_EMAIL_STORAGE_KEY = 'auth_email'
export const AUTH_CURRENT_USER_ID_STORAGE_KEY = 'auth_current_user_id'
export const HEADER_SEARCH_KEY = 'search'
export const USER_PERMISSIONS_STORAGE_KEY = 'user_permissions'
export const USER_LOCALE_STORAGE_KEY = 'user_locale'
export const USER_COUNTRY_STORAGE_KEY = 'user_country'
export const USER_DOCUMENT_TYPES_STORAGE_KEY = 'user_document_types'

export const isMobileApp = isPlatform('capacitor') || isPlatform('cordova')
export const isIos = isMobileApp && isPlatform('ios')
export const isAndroid = isMobileApp && isPlatform('android')

export type Option = { id: UniqueId; label: string } & Record<string, unknown>
export type OptionEnum<T> = { id: T; label: ParseKeys }

export const replacingWipersOptions: { id: string; label: ParseKeys }[] = [
  'replacingWipers.front left',
  'replacingWipers.front right',
  'replacingWipers.rear',
].map((value) => {
  return {
    label: value as ParseKeys,
    id: value,
  }
})

export const replacingHeadlightsOptions: { id: string; label: ParseKeys }[] = [
  'replacingHeadlights.front left headlight',
  'replacingHeadlights.front right headlight',
  'replacingHeadlights.rear left taillight',
  'replacingHeadlights.rear right taillight',
].map((value) => {
  return {
    label: value as ParseKeys,
    id: value,
  }
})

export const diskTypeEnumLabels: Record<DiskTypeEnum, ParseKeys> = {
  [DiskTypeEnum.STAMPED]: 'diskType.stamped',
  [DiskTypeEnum.STAMPED_WITH_HOOD]: 'diskType.stamped with hubcap',
  [DiskTypeEnum.CAST]: 'diskType.alloy',
}
export const diskTypeEnumOptions: OptionEnum<DiskTypeEnum>[] = Object.keys(
  DiskTypeEnum,
).map((value) => {
  const label = diskTypeEnumLabels[value as keyof typeof DiskTypeEnum]
  return {
    label,
    id: value as DiskTypeEnum,
  }
})

export const rubberTypeEnumLabels: Record<RubberTypeEnum, ParseKeys> = {
  [RubberTypeEnum.WINTER]: 'rubberType.winter',
  [RubberTypeEnum.SUMMER]: 'rubberType.summer',
}
export const rubberTypeEnumOptions: OptionEnum<RubberTypeEnum>[] = Object.keys(
  RubberTypeEnum,
).map((value) => {
  const label = rubberTypeEnumLabels[value as keyof typeof RubberTypeEnum]
  return {
    label,
    id: value as RubberTypeEnum,
  }
})

export const wheelCodeEnumLabels: Record<WheelCodeEnum, ParseKeys> = {
  [WheelCodeEnum.FRONT_LEFT]: 'wheelCode.front left wheel',
  [WheelCodeEnum.FRONT_RIGHT]: 'wheelCode.front right wheel',
  [WheelCodeEnum.REAR_LEFT]: 'wheelCode.rear left wheel',
  [WheelCodeEnum.REAR_RIGHT]: 'wheelCode.rear right wheel',
}
export const wheelCodeEnumOptions: OptionEnum<WheelCodeEnum>[] = Object.keys(
  WheelCodeEnum,
).map((value) => {
  const label = wheelCodeEnumLabels[value as keyof typeof WheelCodeEnum]
  return {
    label,
    id: value as WheelCodeEnum,
  }
})

export const documentsCodeEnumLabels: Record<DocumentsTypeEnum, ParseKeys> = {
  [DocumentsTypeEnum.OSAGO]: 'documentsCode.osago',
  [DocumentsTypeEnum.STS]: 'documentsCode.sts',
  [DocumentsTypeEnum.DC]: 'documentsCode.diagnostic card',
  [DocumentsTypeEnum.LICENSE]: 'documentsCode.license',
  [DocumentsTypeEnum.PATENT]: 'documentsCode.patent',
}
export const documentsCodeEnumOptions: OptionEnum<DocumentsTypeEnum>[] =
  Object.keys(DocumentsTypeEnum).map((value) => {
    const label =
      documentsCodeEnumLabels[value as keyof typeof DocumentsTypeEnum]
    return {
      label,
      id: value as DocumentsTypeEnum,
    }
  })

export const incidentResponsibleEnumLabels: Record<
  IncidentResponsibleEnum,
  ParseKeys
> = {
  [IncidentResponsibleEnum.DRIVER]: 'incidentResponsible.driver',
  [IncidentResponsibleEnum.NOT_DRIVER]: 'incidentResponsible.not driver',
  [IncidentResponsibleEnum.UNKNOWN]: 'incidentResponsible.not set',
}
export const incidentResponsibleEnumOptions: OptionEnum<IncidentResponsibleEnum>[] =
  Object.keys(IncidentResponsibleEnum).map((value) => {
    const label =
      incidentResponsibleEnumLabels[
        value as keyof typeof IncidentResponsibleEnum
      ]
    return {
      label,
      id: value as IncidentResponsibleEnum,
    }
  })

export const reasonsTerminations: ParseKeys[] = [
  'reasonsTerminations.emergency repair',
  'reasonsTerminations.days off',
  'reasonsTerminations.debt',
  'reasonsTerminations.accident',
  'reasonsTerminations.unsatisfactory working conditions',
  'reasonsTerminations.technical condition of the car',
  'reasonsTerminations.moved to another city',
  'reasonsTerminations.joined another fleet',
  'reasonsTerminations.aggregator block',
  'reasonsTerminations.car replacement',
  'reasonsTerminations.own car',
  'reasonsTerminations.license revoked',
]

export const reasonsTerminationsOptions = reasonsTerminations.map((reason) => ({
  id: reason,
  label: reason,
}))

export const localeEnumLabels: Record<LocaleEnum, ParseKeys> = {
  [LocaleEnum.ru]: 'locale.russian',
  [LocaleEnum.en]: 'locale.english',
  [LocaleEnum.fr]: 'locale.french',
}

export const localeEnumOptions: OptionEnum<LocaleEnum>[] = Object.keys(
  LocaleEnum,
).map((value) => {
  const label = localeEnumLabels[value as keyof typeof LocaleEnum]
  return {
    label,
    id: value as LocaleEnum,
  }
})
